/**
 * ADR-0210 Slice 9 — Visitor mix on the Dashboard: humans vs AI agents
 * vs crawlers & tools, from one overview read. Answers the founder's
 * question ("which is the highest?") in a sentence, then shows the daily
 * stack and the share of each. Clicking a kind opens the Agents view on
 * that filter. Derived from daily rollups, never signed records.
 */

import { useEffect, useState } from 'react';

import type { AgentKind, AgentOverviewResponse, AgentRollupRange } from '@sill/types/api';
import type { ReportingRange, SiteId } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import type { SetView } from '../../app/view-router.js';
import { useSites } from '../../app/providers.js';
import { Card, EmptyStateCard, Tooltip } from '../../components/atoms/index.js';
import { StackedBars } from '../../components/atoms/StackedBars.js';
import { Activity } from 'lucide-react';
import { useC } from '../../lib/theme.js';
import { fmtDay, fmtInt, fmtPct, monoFont, sansFont } from '../agents/format.js';
import { deltaLabel } from '../agents/Overview.js';

const KINDS: ReadonlyArray<{ key: 'ai' | 'bots' | 'humans'; label: string; noun: string }> = [
  { key: 'humans', label: 'Humans', noun: 'human visitors' },
  { key: 'ai', label: 'AI agents', noun: 'AI agents' },
  { key: 'bots', label: 'Crawlers & tools', noun: 'crawlers & tools' },
];

export function toRollupRange(r: ReportingRange): AgentRollupRange {
  switch (r) {
    case '24h': return '1d';
    case '7d': return '7d';
    case '30d': return '30d';
    default: return '90d';
  }
}

/** The sentence at the top of the card. Exported for tests. */
export function mixStory(totals: AgentOverviewResponse['mix']['totals'], range: AgentRollupRange): string {
  const total = KINDS.reduce((s, k) => s + totals[k.key].traffic, 0);
  if (total === 0) return '';
  const ranked = [...KINDS].sort((a, b) => totals[b.key].traffic - totals[a.key].traffic);
  const top = ranked[0]!;
  const top_share = Math.round((totals[top.key].traffic / total) * 100);
  const ai = totals.ai;
  const ai_share = Math.round((ai.traffic / total) * 100);
  const when = range === '1d' ? 'today' : `in the last ${range.replace('d', ' days')}`;
  const d = deltaLabel(ai.traffic, ai.prior_traffic);
  const ai_move = d.tone === 'flat' ? '' : d.tone === 'new' ? ' — their first appearance' : ` (${d.text} vs the period before)`;
  const lead = `${top.label} were ${top_share}% of page loads ${when}.`;
  if (top.key === 'ai') return `${lead} ${fmtInt(ai.agents)} distinct AI agent${ai.agents === 1 ? '' : 's'} on your busiest day${ai_move}.`;
  return `${lead} AI agents were ${ai_share}%${ai_move}${ai.agents > 0 ? `, from ${fmtInt(ai.agents)} distinct agent${ai.agents === 1 ? '' : 's'} on the busiest day` : ''}.`;
}

export function VisitorMixCard({ range, goTo }: { range: ReportingRange; goTo: SetView }): JSX.Element {
  const C = useC();
  const api = useApi();
  const { activeSite } = useSites();
  const [data, setData] = useState<AgentOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const rr = toRollupRange(range);

  useEffect(() => {
    if (activeSite === null) return;
    let cancelled = false;
    setLoading(true);
    void api.agentRollups.overview(activeSite.site_id as SiteId, rr, 'all').then((res) => {
      if (!cancelled) { setData(res); setLoading(false); }
    }).catch(() => { if (!cancelled) { setData(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [api, activeSite, rr]);

  const mix = data?.mix;
  const total = mix === undefined ? 0 : KINDS.reduce((s, k) => s + mix.totals[k.key].traffic, 0);
  if (!loading && (mix === undefined || total === 0)) {
    return (
      <Card>
        <EmptyStateCard icon={Activity} label="Visitor mix" hint="Humans, AI agents and crawlers appear here once the Sill script or agent endpoints see traffic. Rollups refresh every 15 minutes." />
      </Card>
    );
  }
  const colors: Record<'ai' | 'bots' | 'humans', string> = { humans: C.skyFg, ai: C.limeFg, bots: C.dim };
  const open = (k: AgentKind): void => { goTo('agents', { agent_kind: k }); };

  return (
    <Card className="p-5">
      <div data-testid="visitor-mix" style={{ opacity: loading ? 0.6 : 1 }}>
        <div className="flex items-center justify-between flex-wrap gap-2" style={{ marginBottom: 14 }}>
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: C.muted }}>Visitor mix · humans vs AI agents vs crawlers</div>
          <button type="button" onClick={() => open('all')} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.dim, textTransform: 'uppercase' }}>Open agents →</button>
        </div>
        {mix !== undefined && total > 0 ? (
          <>
            <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.55, marginBottom: 18, maxWidth: 640 }} data-testid="visitor-mix-story">{mixStory(mix.totals, rr)}</div>
            <div style={{ display: 'flex', height: 10, borderRadius: 2, overflow: 'hidden', background: C.surface2, marginBottom: 18 }}>
              {KINDS.map((k) => {
                const v = mix.totals[k.key].traffic;
                if (v === 0) return null;
                return (
                  <Tooltip key={k.key} content={`${k.label}: ${fmtInt(v)} · ${fmtPct(v, total)}`} displayMode="block" style={{ width: `${(v / total) * 100}%` }}>
                    <div style={{ height: 10, background: colors[k.key], opacity: 0.9 }} />
                  </Tooltip>
                );
              })}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" style={{ marginBottom: 20 }}>
              {KINDS.map((k) => {
                const t = mix.totals[k.key];
                const d = deltaLabel(t.traffic, t.prior_traffic);
                const good = d.tone === 'up';
                return (
                  <button
                    key={k.key}
                    type="button"
                    onClick={() => open(k.key)}
                    style={{ textAlign: 'left', padding: '12px 14px', border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface2, cursor: 'pointer' }}
                    data-testid={`mix-${k.key}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: C.muted }}>
                      <span style={{ width: 8, height: 8, borderRadius: 1, background: colors[k.key], display: 'inline-block' }} />{k.label}
                    </div>
                    <div style={{ fontFamily: monoFont, fontSize: 20, fontWeight: 600, color: C.text, marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>{fmtInt(t.traffic)}</div>
                    <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginTop: 4 }}>{fmtPct(t.traffic, total)} <span style={{ color: d.tone === 'flat' ? C.dim : d.tone === 'new' ? C.skyFg : good ? C.limeFg : C.coralFg }}>{d.text !== '—' ? d.text : ''}</span></div>
                  </button>
                );
              })}
            </div>
            <StackedBars
              days={mix.days}
              series={KINDS.map((k) => ({ label: k.label, values: mix.series[k.key], color: colors[k.key] }))}
              width={720}
              height={140}
              formatDay={fmtDay}
              formatValue={fmtInt}
              aria-label="Daily page loads by visitor kind"
            />
            <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.dim, textTransform: 'uppercase', marginTop: 12 }}>
              Page loads · derived from daily rollups · not signed records
            </div>
          </>
        ) : (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>LOADING…</div>
        )}
      </div>
    </Card>
  );
}
